/**
 * Unified video view page component
 * Supports both tracked (with recipient/analytics) and public (anonymous) modes
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { Share as ShareIcon, Download as DownloadIcon } from "@mui/icons-material";

// Android share sheet doesn't offer "Save to device", so we use a download flow
// (and label) instead of share on Android. Detected once at module load.
const IS_ANDROID = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

import { VideoPlayer } from "./VideoPlayer";
import BunnyStreamPlayer from "@/components/project/BunnyStreamPlayer";
import { CookieConsentBanner } from "./CookieConsentBanner";
import { VideoNotFoundError } from "./VideoNotFoundError";
import { VideoNotYetUploadedError } from "./VideoNotYetUploadedError";
import { VideoServerError } from "./VideoServerError";
import { useAnalytics } from "@/hooks/useAnalytics";
import { fetchVideoShare } from "@/api/videoShareApi";
import { fetchPublicVideo } from "@/api/publicVideoApi";
import { ApiError } from "@/types/Api";
import BrandedContainer from "@/components/guest/BrandedContainer";
import { trackEvent } from "@/utils/analyticsTracking";

interface TrackedModeProps {
  mode: "tracked";
  shareKey: string;
}

interface PublicModeProps {
  mode: "public";
  slug: string;
}

type VideoViewPageProps = TrackedModeProps | PublicModeProps;

// Unified data structure for rendering
interface VideoViewData {
  video: {
    id: string;
    project_id: string;
    download_url: string | null;
    storage_provider: string;
    embed_url: string | null;
    encoding_status: string | null;
    encoding_progress: number | null;
  };
  project: {
    name: string;
    logo_url: string | null;
    banner_url: string | null;
    background_url: string | null;
    colors?: {
      form_background: string;
      text_color: string;
      button_gradient_start: string;
      button_gradient_end: string;
      button_text_color: string;
    } | null;
  };
  recipientName: string | null;
}

export function VideoViewPage(props: VideoViewPageProps) {
  const isTracked = props.mode === "tracked";
  const identifier = isTracked ? props.shareKey : props.slug;

  // Analytics only for tracked mode (pass empty string for public mode - hook won't be used)
  const { hasChoice, track, acceptConsent, declineConsent } = useAnalytics(
    isTracked ? identifier : ""
  );
  const [isSharing, setIsSharing] = useState(false);

  // Fetch data based on mode
  const {
    data: trackedData,
    isLoading: trackedLoading,
    error: trackedError,
  } = useQuery({
    queryKey: ["videoShare", identifier],
    queryFn: () => fetchVideoShare(identifier),
    enabled: isTracked,
  });

  const {
    data: publicData,
    isLoading: publicLoading,
    error: publicError,
  } = useQuery({
    queryKey: ["publicVideo", identifier],
    queryFn: () => fetchPublicVideo(identifier),
    enabled: !isTracked,
  });

  // Normalize data to unified structure — memoized to keep stable references
  // and prevent re-render cascades (iOS stack overflow, Sentry GLAMBOT-PROJECT-MANAGER-2M)
  const data: VideoViewData | null = useMemo(() => {
    if (isTracked) {
      if (!trackedData) return null;
      return {
        video: {
          id: trackedData.video.id ?? "",
          project_id: trackedData.video.project_id ?? "",
          download_url: trackedData.video.download_url,
          storage_provider: trackedData.video.storage_provider || "s3",
          embed_url: trackedData.video.embed_url || null,
          encoding_status: trackedData.video.encoding_status || null,
          encoding_progress: trackedData.video.encoding_progress ?? null,
        },
        project: trackedData.project,
        recipientName: trackedData.recipient.name,
      };
    }
    if (!publicData) return null;
    return {
      video: {
        id: publicData.video.id ?? "",
        project_id: publicData.video.project_id ?? "",
        download_url: publicData.video.download_url,
        storage_provider: publicData.video.storage_provider || "s3",
        embed_url: publicData.video.embed_url || null,
        encoding_status: publicData.video.encoding_status || null,
        encoding_progress: publicData.video.encoding_progress ?? null,
      },
      project: publicData.project,
      recipientName: null,
    };
  }, [isTracked, trackedData, publicData]);

  const isLoading = isTracked ? trackedLoading : publicLoading;
  const error = isTracked ? trackedError : publicError;

  // Track page view ONCE when component mounts (only for tracked mode)
  const pageViewTrackedRef = useRef(false);
  useEffect(() => {
    if (isTracked && !pageViewTrackedRef.current) {
      pageViewTrackedRef.current = true;
      track("page_view");
    }
  }, [isTracked, track]);

  // Track video:view after 3 seconds of data being available
  // Depend on primitive IDs (not the whole data object) to avoid timer restarts on re-renders
  const viewTrackedRef = useRef(false);
  const videoId = data?.video.id || null;
  const projectId = data?.video.project_id || null;
  useEffect(() => {
    if (!videoId || !projectId || viewTrackedRef.current) return;

    const timer = setTimeout(() => {
      trackEvent("video:view", "video", videoId, projectId);
      viewTrackedRef.current = true;
    }, 3000);

    return () => clearTimeout(timer);
  }, [videoId, projectId]);

  // Handle share - use native share API with video file, or fallback to download
  const handleShare = async () => {
    if (!data || isSharing) return;

    setIsSharing(true);
    try {
      if (isTracked) {
        track("video_share");
      }

      // Try Web Share API with video file (iOS — allows save to camera roll + share)
      if (!IS_ANDROID && data.video.download_url && navigator.share) {
        try {
          const response = await fetch(data.video.download_url);
          const blob = await response.blob();
          const file = new File([blob], `${data.project.name || "video"}.mp4`, {
            type: "video/mp4",
          });

          await navigator.share({
            title: data.project.name,
            text: `Check out this video from ${data.project.name}`,
            files: [file],
          });
          return;
        } catch (err) {
          // AbortError = user cancelled the share dialog
          if (err instanceof Error && err.name === "AbortError") return;
          console.log("File share failed, falling back:", err);
        }
      }

      // Fallback: share URL only (no file) — skipped on Android (goes to direct download)
      if (!IS_ANDROID && navigator.share) {
        try {
          await navigator.share({
            title: data.project.name,
            text: `Check out this video from ${data.project.name}`,
            url: window.location.href,
          });
          return;
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") return;
          console.log("URL share failed:", err);
        }
      }

      // Desktop fallback: force download using blob
      if (data.video.download_url) {
        if (isTracked) {
          track("video_download");
        }
        try {
          const response = await fetch(data.video.download_url);
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${data.project.name || "video"}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error("Download failed:", err);
          window.open(data.video.download_url, "_blank");
        }
      } else {
        // No download URL and no share API — copy link
        try {
          await navigator.clipboard.writeText(window.location.href);
        } catch {
          // Clipboard API may fail without HTTPS or focus — ignore silently
        }
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !data) {
    const is404 = error instanceof ApiError && error.status === 404;
    if (is404) {
      // Public mode: video may not be uploaded yet
      // Tracked mode: link expired or removed
      return isTracked ? <VideoNotFoundError /> : <VideoNotYetUploadedError />;
    }
    return <VideoServerError error={error} />;
  }

  return (
    <>
      {/* Cookie Consent Banner - only for tracked mode */}
      {isTracked && (
        <CookieConsentBanner
          show={!hasChoice}
          onAccept={acceptConsent}
          onDecline={declineConsent}
        />
      )}

      <BrandedContainer
        project={data.project}
        maxWidth={900}
        cardBackgroundColor={data.project.colors?.form_background}
        textColor={data.project.colors?.text_color}
      >
        {/* Greeting - Personalized for tracked, generic for public */}
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 500,
            color: data.project.colors?.text_color || "text.primary",
            mb: 1,
          }}
        >
          {data.recipientName ? `Hi ${data.recipientName}!` : "Welcome!"}
        </Typography>

        {/* Subheading */}
        <Typography
          variant="h6"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 400,
            color: data.project.colors?.text_color || "text.secondary",
            opacity: data.project.colors?.text_color ? 0.7 : 1,
            mb: 4,
          }}
        >
          Here's your video
        </Typography>

        {/* Video Player — Bunny or S3 */}
        <Box sx={{ mb: 4 }}>
          {data.video.storage_provider === "bunny" &&
          data.video.encoding_status === "ready" &&
          data.video.embed_url ? (
            <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
              <BunnyStreamPlayer embedUrl={data.video.embed_url} />
            </Box>
          ) : data.video.storage_provider === "bunny" ? (
            // Bunny video without a usable embed_url: show status placeholder.
            // Never fall through to VideoPlayer with the /original URL — that
            // causes iOS Safari to download the full uncompressed MP4 on
            // pageload (Sentry: performance_large_http_payload).
            <Box
              sx={{
                position: "relative",
                paddingTop: "56.25%",
                backgroundColor: "black",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "grey.400",
                }}
              >
                <Typography>
                  {data.video.encoding_status === "failed"
                    ? "This video could not be processed."
                    : data.video.encoding_status !== "ready"
                      ? `Video is being processed${data.video.encoding_progress != null ? ` (${data.video.encoding_progress}%)` : ""}...`
                      : "Video playback is temporarily unavailable."}
                </Typography>
              </Box>
            </Box>
          ) : data.video.download_url ? (
            <VideoPlayer
              videoUrl={data.video.download_url}
              poster={data.project.banner_url || undefined}
            />
          ) : (
            <Box
              sx={{
                position: "relative",
                paddingTop: "56.25%",
                backgroundColor: "black",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "grey.400",
                }}
              >
                <Typography>Video not available</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Share / Download Button (Android shows Download since share sheet has no save action) */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={
            isSharing ? (
              <CircularProgress size={20} color="inherit" />
            ) : IS_ANDROID ? (
              <DownloadIcon />
            ) : (
              <ShareIcon />
            )
          }
          onClick={handleShare}
          disabled={isSharing}
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            py: 1.5,
            borderRadius: 2,
            ...(data.project.colors && {
              background: `linear-gradient(135deg, ${data.project.colors.button_gradient_start}, ${data.project.colors.button_gradient_end})`,
              color: data.project.colors.button_text_color,
              "&:hover": {
                background: `linear-gradient(135deg, ${data.project.colors.button_gradient_start}, ${data.project.colors.button_gradient_end})`,
                filter: "brightness(1.1)",
              },
              "&.Mui-disabled": {
                background: `linear-gradient(135deg, ${data.project.colors.button_gradient_start}, ${data.project.colors.button_gradient_end})`,
                color: data.project.colors.button_text_color,
                opacity: 0.5,
              },
            }),
          }}
        >
          {isSharing
            ? "Preparing video..."
            : IS_ANDROID
              ? "Download Video"
              : "Share Video"}
        </Button>
      </BrandedContainer>
    </>
  );
}
